import { X, Plus } from "lucide-react";
import { useState } from "react";
import { useSwipeable } from "react-swipeable";
import { useFilterUnit } from "@/hooks/useFilter";
import { useSpices } from "@/hooks/useSpices";

// ============================================================================
// Types & Interfaces
// ============================================================================

interface SpiceItemProps {
  item: { name: string; id: number; thumbnail: string };
  isSelected: boolean;
  displayQuantity: number;
  hasQuantity: boolean;
  onSelect: (id: number) => void;
}

interface ItemImageProps {
  src: string;
  alt: string;
}

interface ItemLabelProps {
  name: string;
}

interface SpiceProps {
  src: string;
  alt: string;
  name: string;
}

interface QuantityTextProps {
  displayQuantity: number;
  abbreviatedUnit: string;
}

interface DeleteButtonProps {
  itemId: number;
}

interface AddButtonProps {
  itemId: number;
  onSelect: (id: number) => void;
}

// ============================================================================
// Utilities
// ============================================================================

// Unit abbreviations mapping
const UNIT_ABBREVIATIONS: Record<string, string> = {
  tsp: "tsp",
  tbsp: "tbsp",
  gram: "g",
};

function abbreviateUnit(unit: string): string {
  return UNIT_ABBREVIATIONS[unit.toLowerCase()] || unit;
}

// ============================================================================
// Subcomponents
// ============================================================================

function ItemImage({ src, alt }: ItemImageProps) {
  return (
    <img
      src={src}
      loading="lazy"
      alt={alt}
      className="item-image w-8 h-8 rounded-full object-cover shrink-0"
    />
  );
}

function ItemLabel({ name }: ItemLabelProps) {
  return (
    <span className="item-label text-sm font-medium truncate ml-2 max-w-48 lg:@[280px]:max-w-16">
      {name}
    </span>
  );
}

function Spice({ src, alt, name }: SpiceProps) {
  return (
    <div className="flex items-center">
      <ItemImage src={src} alt={alt} />
      <ItemLabel name={name} />
    </div>
  );
}

function QuantityText({
  displayQuantity,
  abbreviatedUnit,
}: QuantityTextProps) {
  return (
    <span className="quantity-text text-xs font-medium whitespace-nowrap">
      {displayQuantity}
      {abbreviatedUnit}
    </span>
  );
}

function DeleteButton({ itemId }: DeleteButtonProps) {
  const { removeSpice } = useSpices();

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    removeSpice(itemId);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      removeSpice(itemId);
    }
  };

  return (
    <div
      onClick={(handleClick)}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      className="delete-button max-lg:absolute flex items-center w-0 lg:group-hover:w-fit h-full rounded-full transition-all shrink-0 cursor-pointer max-lg:opacity-100 opacity-0 group-hover:opacity-100 hover:bg-gray-100"
      aria-label="Remove spice"
    >
      <X size={14} className="w-0 group-hover:w-fit"/>
    </div>
  );
}

function AddButton({ itemId, onSelect }: AddButtonProps) {
  const { addNew } = useSpices();

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setTimeout(() => {
      addNew(itemId);
      onSelect(itemId);
    }, 120);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      addNew(itemId);
      onSelect(itemId);
    }
  };

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      className="lg:hidden add-button flex items-center w-fit h-full rounded-full transition-all shrink-0 cursor-pointer hover:bg-gray-100 active:scale-90 active:opacity-60"
      aria-label="Add spice"
    >
       <Plus size={14} className="max-lg:border-1 max-lg:w-10 max-lg:h-10 max-lg:p-2 max-lg:rounded-full"/>

    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SpiceItem({
  item,
  isSelected,
  displayQuantity,
  hasQuantity,
  onSelect,
}: SpiceItemProps) {
  const [unit] = useFilterUnit();
  const { removeSpice, addNew } = useSpices();
  const abbreviatedUnit = abbreviateUnit(unit);
  const [isSwiped, setIsSwiped] = useState(false);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => hasQuantity && setIsSwiped(true),
    onSwipedRight: () => {
      if (hasQuantity) {
        setIsSwiped(false);
      } else {
        addNew(item.id);
        onSelect(item.id);
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: false,
    delta: 50,
  });

  const handleSwipeDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeSpice(item.id);
    setIsSwiped(false);
  };

  return (
    <div
      {...swipeHandlers}
      className={`item-wrapper relative group overflow-hidden ${
        isSelected ? "text-main bg-secondary" : "text-secondary bg-primary"
      }`}
    >
      <button
        onClick={() => onSelect(item.id)}
        onKeyDown={(e) => e.key === "Enter" && onSelect(item.id)}
        type="button"
        className="item-button max-lg:relative flex items-center justify-between w-full h-[50px] px-2 rounded-lg cursor-pointer outline-none"
      >
        <div className={`min-w-0 group-hover:max-w-32 ${!hasQuantity ? "opacity-60" : ""}`}>
          <Spice src={item.thumbnail} alt={item.name} name={item.name} />
        </div>

        {hasQuantity && (
          <div className={`flex items-center gap-2 transition-transform ${
            isSwiped ? "-translate-x-16" : "translate-x-0"
          }`}>
            <QuantityText displayQuantity={displayQuantity} abbreviatedUnit={abbreviatedUnit} />
            <DeleteButton itemId={item.id} />
          </div>
        )}

        {!hasQuantity && !isSelected && <AddButton itemId={item.id} onSelect={onSelect} />}
      </button>

      {hasQuantity && (
        <div className={`absolute right-0 top-0 h-full flex items-center justify-center bg-red-500 lg:hidden transition-all overflow-hidden ${
          isSwiped ? "w-16" : "w-0"
        }`}>
          <button
            onClick={handleSwipeDelete}
            className="text-white"
            aria-label="Delete"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}
